import { useCallback, useEffect, useRef, useState } from 'react';
import {
    PublicBulkUpdateAnswerItem,
    submitSessionShareAnswers,
} from '../../../api/publicSessionApi';
import {
    QuestionTypeFrontend,
    SessionQuestionEntityFrontend,
    WorkerDtoFrontend,
    getAnswerForQuestion,
} from '../../../api/sessionQuestionsApi';
import logger from '../../../utils/logger';
import { buildInitialValue, normalizeQuestionAnswerForSubmission } from '../utils';
import { TranslateFn } from './useSessionFormI18n';

export interface PersistOptions {
    refreshAfter?: boolean;
    successMessage?: string;
}

export type ShowSnackbarFn = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning',
) => void;

export interface PersistenceState {
    formValues: Record<number, any>;
    autoSaving: boolean;
    submitting: boolean;
    dirty: boolean;
    terminalOutcome: 'GRANTED' | 'DECLINED' | null;
    setValue: (questionId: number, value: any) => void;
    /** Persist a single field's normalized current value (used on blur). */
    persistFieldOnBlur: (question: SessionQuestionEntityFrontend) => void;
    /**
     * Persist a value immediately at the moment of change (used by Radio /
     * Boolean / MULTISELECT where blur isn't a natural commit boundary).
     */
    persistImmediate: (
        question: SessionQuestionEntityFrontend,
        newValue: string,
    ) => Promise<void>;
    /** Persist a list-of-objects JSON blob from the dedicated edit modal. */
    persistListObject: (
        question: SessionQuestionEntityFrontend,
        answerJson: string,
    ) => Promise<void>;
    /** Final-submit handler: validates, batches dirty fields, submits. */
    submitAll: () => Promise<void>;
    /** Reset all values back to what the server returned. */
    resetToServerValues: () => void;
}

export interface UsePersistenceParams {
    publicShareId: string | undefined;
    questions: SessionQuestionEntityFrontend[];
    worker: WorkerDtoFrontend | null;
    /** Called when the persisted update should be followed by a fresh data load. */
    onAfterPersistRefresh: () => Promise<void>;
    showSnackbar: ShowSnackbarFn;
    t: TranslateFn;
}

/**
 * Question types that the form renders OUT-OF-BAND of the FormSection list
 * (so the worker never sees them as form rows):
 *   - LANGUAGE_SELECTION lives in the welcome-card LanguagePicker.
 *   - CV_UPLOAD lives in the UploadFirstStep widget.
 * Submit-time validation must skip these — otherwise a required CV_UPLOAD
 * row that the worker has no way to interact with locks the submit button
 * with "Please provide an answer for 'Upload CV'", which is exactly the
 * dead-end the user hit on 2026-06-29.
 *
 * Kept here (mirroring the page's `groupedQuestions` filter) rather than
 * lifted into a shared module because the persistence hook is the only
 * other place that needs it — duplicating two lines beats round-tripping
 * a Set prop through every component layer.
 */
const HIDDEN_FROM_FORM: Set<QuestionTypeFrontend> = new Set([
    QuestionTypeFrontend.LANGUAGE_SELECTION,
    QuestionTypeFrontend.CV_UPLOAD,
]);

/**
 * Owns the in-memory form values, dirty tracking, autosave, and the final
 * batch submit. Extracted from the page so the renderable tree can stay thin
 * and so the S2 (optimistic-UI rollback) fix has a single place to live.
 *
 * Behavior currently matches the prior in-page implementation. The S2 fix
 * (only mutate the baseline after backend confirms) and S6 fix (stale-
 * response guard) will refine this in subsequent steps without leaking into
 * the orchestrator.
 */
export const useSessionFormPersistence = ({
    publicShareId,
    questions,
    worker,
    onAfterPersistRefresh,
    showSnackbar,
    t,
}: UsePersistenceParams): PersistenceState => {
    const [formValues, setFormValues] = useState<Record<number, any>>({});
    const [autoSaving, setAutoSaving] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [dirty, setDirty] = useState<boolean>(false);
    const [terminalOutcome, setTerminalOutcome] = useState<
        'GRANTED' | 'DECLINED' | null
    >(null);
    const initialAnswersRef = useRef<Record<number, string>>({});

    // Re-hydrate on every fresh question list. Server-projected worker values
    // become the in-memory initial values, and the same projections become
    // the "original" reference used by dirty-detection.
    useEffect(() => {
        const initialValues: Record<number, any> = {};
        const normalizedInitials: Record<number, string> = {};
        questions.forEach((question) => {
            const projected = getAnswerForQuestion(question, worker);
            const initial = buildInitialValue(question, projected);
            initialValues[question.id] = initial;
            normalizedInitials[question.id] = normalizeQuestionAnswerForSubmission(
                question,
                initial,
            );
        });
        setFormValues(initialValues);
        initialAnswersRef.current = normalizedInitials;
        setDirty(false);
    }, [questions, worker]);

    const setValue = useCallback((questionId: number, value: any) => {
        setFormValues((prev) => ({ ...prev, [questionId]: value }));
    }, []);

    const recomputeDirty = useCallback(() => {
        const hasDirty = questions.some((question) => {
            const currentValue = formValues[question.id];
            const normalized = normalizeQuestionAnswerForSubmission(question, currentValue);
            const original = initialAnswersRef.current[question.id] ?? '';
            return normalized !== original;
        });
        setDirty(hasDirty);
    }, [questions, formValues]);

    useEffect(() => {
        recomputeDirty();
    }, [recomputeDirty]);

    const shouldRefreshAfterSave = useCallback(
        (question: SessionQuestionEntityFrontend) =>
            // PROFESSION_SELECTION mutates the worker's profession_key, which
            // changes the question set the worker sees next — must refetch.
            // LANGUAGE_SELECTION mutates sessions.language (S7); the chrome's
            // `t()` lookups read sessionSummary.language, so without a refetch
            // here the form persists the answer but the UI stays in the old
            // language until the worker manually reloads. Refetching is the
            // signal that flips every translated string in one pass.
            question.questionType === QuestionTypeFrontend.PROFESSION_SELECTION ||
            question.questionType === QuestionTypeFrontend.LANGUAGE_SELECTION,
        [],
    );

    const persistAnswers = useCallback(
        async (updates: PublicBulkUpdateAnswerItem[], options?: PersistOptions): Promise<boolean> => {
            if (!publicShareId) {
                showSnackbar(t('snackbar_share_missing'), 'error');
                return false;
            }
            if (updates.length === 0) {
                return true;
            }
            setAutoSaving(true);
            try {
                // CRITICAL: do NOT mutate `initialAnswersRef` before this await
                // resolves. A backend rejection must leave the original baseline
                // in place so the form still considers the field dirty and a
                // subsequent submit retries the write. Reordering these lines
                // re-introduces the silent-data-loss bug we explicitly fixed.
                await submitSessionShareAnswers(publicShareId, updates);

                updates.forEach(({ sessionQuestionId, answer }) => {
                    initialAnswersRef.current[sessionQuestionId] = answer;
                });

                if (options?.refreshAfter) {
                    await onAfterPersistRefresh();
                }

                if (options?.successMessage) {
                    showSnackbar(options.successMessage, 'success');
                }

                if (!options?.refreshAfter) {
                    recomputeDirty();
                }
                return true;
            } catch (err: any) {
                logger.error('Failed to submit public session answers', err);
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    t('snackbar_save_failed');
                showSnackbar(message, 'error');
                // Defensive: explicitly re-assert dirty state after a rejection.
                // The ref was never mutated (see CRITICAL note above), but a
                // future regression that flips the ordering would silently
                // mark the form clean — this call surfaces that as `dirty=true`
                // instead and keeps the submit button enabled.
                recomputeDirty();
                return false;
            } finally {
                setAutoSaving(false);
            }
        },
        [publicShareId, onAfterPersistRefresh, showSnackbar, recomputeDirty, t],
    );

    const persistFieldOnBlur = useCallback(
        (question: SessionQuestionEntityFrontend) => {
            const currentValue = formValues[question.id];
            const normalized = normalizeQuestionAnswerForSubmission(question, currentValue);
            const original = initialAnswersRef.current[question.id] ?? '';
            if (normalized === original) {
                return;
            }
            void persistAnswers(
                [{ sessionQuestionId: question.id, answer: normalized }],
                { refreshAfter: shouldRefreshAfterSave(question) },
            );
        },
        [formValues, persistAnswers, shouldRefreshAfterSave],
    );

    const persistImmediate = useCallback(
        async (question: SessionQuestionEntityFrontend, newValue: string) => {
            const normalized = normalizeQuestionAnswerForSubmission(question, newValue);
            await persistAnswers(
                [{ sessionQuestionId: question.id, answer: normalized }],
                { refreshAfter: shouldRefreshAfterSave(question) },
            );
        },
        [persistAnswers, shouldRefreshAfterSave],
    );

    const persistListObject = useCallback(
        async (question: SessionQuestionEntityFrontend, answerJson: string) => {
            setValue(question.id, answerJson);
            await persistAnswers(
                [{ sessionQuestionId: question.id, answer: answerJson }],
                { refreshAfter: shouldRefreshAfterSave(question) },
            );
        },
        [persistAnswers, setValue, shouldRefreshAfterSave],
    );

    const submitAll = useCallback(async () => {
        if (!publicShareId) {
            showSnackbar(t('snackbar_share_missing'), 'error');
            return;
        }

        const validationErrors: string[] = [];
        const normalizedMap: Record<number, string> = {};
        const consentQuestion = questions.find(
            (question) => question.questionKey === 'ACTION_CONSENT',
        );
        const normalizedConsent = consentQuestion
            ? normalizeQuestionAnswerForSubmission(
                  consentQuestion,
                  formValues[consentQuestion.id],
              )
            : '';
        const isDecline = normalizedConsent === 'no' || normalizedConsent === 'false';

        questions.forEach((question) => {
            const currentValue = formValues[question.id];
            const normalized = normalizeQuestionAnswerForSubmission(question, currentValue);
            normalizedMap[question.id] = normalized;

            // Refusal is an independent legal choice. Requiring a candidate
            // to finish the dossier before they can say no is coercive and
            // contradicts the consent contract; validate only that decision.
            if (isDecline && question.questionKey !== 'ACTION_CONSENT') {
                return;
            }

            // Questions handled out-of-band (LanguagePicker writes
            // LANGUAGE_SELECTION via persistImmediate; UploadFirstStep
            // handles CV_UPLOAD via the dedicated file-upload endpoint)
            // never enter the worker's form view — validating them here
            // would lock the submit button on an answer the worker can't
            // provide.
            if (HIDDEN_FROM_FORM.has(question.questionType)) {
                return;
            }

            const label = question.questionShorten || question.question;

            if (question.isRequiredAnswer) {
                const emptyValues = ['', '[]', 'null', null, undefined];
                // On the FINAL_PROFESSION_CONFIRMATION gate, the bare 'yes' is a
                // transient UI-only intent that just reveals the profession
                // dropdown — it is never a resolved answer. Only 'no' (the
                // NO_OTHER_PROFESSIONS sentinel) or an actual profession add
                // (which resets the gate) counts as answered, so treat a
                // lingering 'yes' as unanswered instead of a valid non-empty value.
                const isUnresolvedProfessionGate =
                    question.questionType ===
                        QuestionTypeFrontend.FINAL_PROFESSION_CONFIRMATION &&
                    normalized === 'yes';
                if (emptyValues.includes(normalized) || isUnresolvedProfessionGate) {
                    validationErrors.push(
                        t('validation_required_field', { question: label }),
                    );
                }
            }

            if (question.answerType === 'LIST_OBJECT' && normalized) {
                try {
                    const parsed = JSON.parse(normalized);
                    if (!Array.isArray(parsed)) {
                        validationErrors.push(
                            t('validation_list_object_array', { question: label }),
                        );
                    }
                } catch {
                    validationErrors.push(
                        t('validation_list_object_invalid_json', { question: label }),
                    );
                }
            }

            if (question.answerType === 'MULTISELECT' && normalized) {
                try {
                    const parsed = JSON.parse(normalized);
                    if (!Array.isArray(parsed)) {
                        validationErrors.push(
                            t('validation_multiselect_array', { question: label }),
                        );
                    }
                } catch {
                    validationErrors.push(
                        t('validation_multiselect_invalid_json', { question: label }),
                    );
                }
            }
        });

        if (validationErrors.length > 0) {
            showSnackbar(validationErrors[0], 'error');
            return;
        }

        const updates: PublicBulkUpdateAnswerItem[] = questions
            .map((question) => {
                if (isDecline && question.questionKey !== 'ACTION_CONSENT') {
                    return null;
                }
                // Don't bulk-submit out-of-band question types. LANGUAGE_SELECTION
                // is written by the picker via persistImmediate the moment the
                // worker picks; CV_UPLOAD has no form-rendered control to
                // produce a value. Including either here would either re-send
                // a stale value or send '' for an unanswered required field.
                if (HIDDEN_FROM_FORM.has(question.questionType)) {
                    return null;
                }
                const normalized = normalizedMap[question.id];
                const original = initialAnswersRef.current[question.id] ?? '';
                if (normalized === original) {
                    return null;
                }
                return { sessionQuestionId: question.id, answer: normalized };
            })
            .filter((item): item is PublicBulkUpdateAnswerItem => Boolean(item));

        if (updates.length === 0) {
            showSnackbar(t('snackbar_no_changes'), 'info');
            return;
        }

        setSubmitting(true);
        try {
            const persisted = await persistAnswers(updates, {
                // Both consent outcomes revoke the bearer link. Rendering the
                // committed reviewed closing copy locally avoids replacing a
                // successful decision with a generic 410 page.
                refreshAfter: !consentQuestion,
                successMessage: consentQuestion
                    ? undefined
                    : t('snackbar_thanks_saved'),
            });
            if (persisted && consentQuestion) {
                setTerminalOutcome(isDecline ? 'DECLINED' : 'GRANTED');
            }
        } finally {
            setSubmitting(false);
        }
    }, [publicShareId, questions, formValues, persistAnswers, showSnackbar, t]);

    const resetToServerValues = useCallback(() => {
        void onAfterPersistRefresh();
    }, [onAfterPersistRefresh]);

    return {
        formValues,
        autoSaving,
        submitting,
        dirty,
        terminalOutcome,
        setValue,
        persistFieldOnBlur,
        persistImmediate,
        persistListObject,
        submitAll,
        resetToServerValues,
    };
};
